import { t } from "ttag";

import noResultsSource from "assets/img/no_results.svg";
import {
  type IllustrationValue,
  PLUGIN_LOGO_ICON_COMPONENTS,
  PLUGIN_SELECTORS,
} from "metabase/plugins";
import type { State } from "metabase/redux/store";
import { getSetting } from "metabase/settings";
import type {
  IllustrationSettingValue,
  LoadingMessage,
} from "metabase-types/api";

import { SettingsLogoIcon } from "./SettingsLogoIcon";

/**
 * The application's appearance from its settings. Metabase's open-source plugins answer with
 * the Metabase defaults whatever the appearance settings say; this one reads them, so that the
 * name, the logo, the loading message, the illustrations, and the links the settings name are
 * what the application shows. The settings themselves are plain in this build
 * (`metabase.appearance.settings`), set from the environment or through the settings API.
 */

// The name the application has when nothing renames it; the same literal as the setting's default
// on the server and in the open-source plugin's selectors.
// eslint-disable-next-line metabase/no-literal-metabase-strings -- the product's own name
export const METABASE_NAME = "Metabase";

// The drawing of the sign-in and landing pages when the setting says `default`: the same path the
// open-source plugin's selectors name.
const DEFAULT_PAGE_ILLUSTRATION: IllustrationValue = {
  src: "app/img/bridge.svg",
  isDefault: true,
};

const LOADING_MESSAGES: Record<LoadingMessage, (isSlow: boolean) => string> = {
  "doing-science": (isSlow) =>
    isSlow ? t`Waiting for results...` : t`Doing science...`,
  "running-query": (isSlow) =>
    isSlow ? t`Waiting for results...` : t`Running query...`,
  "loading-results": (isSlow) =>
    isSlow ? t`Waiting for results...` : t`Loading results...`,
};

/** A page's illustration from its choice (`default`, `none`, `custom`) and the custom image. */
export function pageIllustration(
  choice: IllustrationSettingValue | undefined,
  custom: string | undefined,
): IllustrationValue {
  switch (choice) {
    case "none":
      return null;
    case "custom":
      return custom ? { src: custom, isDefault: false } : null;
    default:
      return DEFAULT_PAGE_ILLUSTRATION;
  }
}

/** A result's illustration (no data, no object found) from its choice and the custom image. */
export function resultIllustration(
  choice: IllustrationSettingValue | undefined,
  custom: string | undefined,
): string | null {
  switch (choice) {
    case "none":
      return null;
    case "custom":
      return custom || null;
    default:
      return noResultsSource;
  }
}

export const getApplicationNameFromSettings = (state: State): string =>
  getSetting(state, "application-name") || METABASE_NAME;

export function initializeAppearanceFromSettings() {
  Object.assign(PLUGIN_SELECTORS, {
    getApplicationName: getApplicationNameFromSettings,
    getIsWhiteLabeling: (state: State) =>
      getApplicationNameFromSettings(state) !== METABASE_NAME,
    getShowMetabaseLinks: (state: State) =>
      getSetting(state, "show-metabase-links") !== false,
    getLoadingMessageFactory: (state: State) =>
      LOADING_MESSAGES[getSetting(state, "loading-message")] ??
      LOADING_MESSAGES["doing-science"],
    getLoginPageIllustration: (state: State) =>
      pageIllustration(
        getSetting(state, "login-page-illustration"),
        getSetting(state, "login-page-illustration-custom"),
      ),
    getLandingPageIllustration: (state: State) =>
      pageIllustration(
        getSetting(state, "landing-page-illustration"),
        getSetting(state, "landing-page-illustration-custom"),
      ),
    getNoDataIllustration: (state: State) =>
      resultIllustration(
        getSetting(state, "no-data-illustration"),
        getSetting(state, "no-data-illustration-custom"),
      ),
    getNoObjectIllustration: (state: State) =>
      resultIllustration(
        getSetting(state, "no-object-illustration"),
        getSetting(state, "no-object-illustration-custom"),
      ),
  });
  PLUGIN_LOGO_ICON_COMPONENTS.push(SettingsLogoIcon);
}
